import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import { Archive, FileOutline, PaperPlane, SlashOutline, Undo2Outline } from '@lyearn/icons';
import { SkillEntityConfig } from '@/components/FilterManager/EntityFilter/entity/skills';
import {
  getAlphabeticallySortItem,
  getDefaultSortValue,
  getLastEditedSortItem,
  getNewestSortItem,
  getOldestSortItem,
  getRelevanceSortItem,
} from '@/components/FilterManager/SortMenu/utils';
import { FilterManager, SortAndFilterItem } from '@/components/FilterManager/types';
import { useFilterDummyValue } from '@/components/FilterNew/hooks/useDummies';
import { getUseOptionsQuery } from '@/components/FilterNew/utils/getUseOptionsQuery';
import { FeatureFlagsMap } from '@/helper/FeatureFlags';
import { AppConfig } from '@/hooks/useAppConfig';
import { ResourceStatusEnum } from '@/types/schema';

import { LearningContentPageFilters } from '../types';
import { getContentTypeOptions } from './getContentType';
import { getMoreFilterOptions, pillsConfig } from './getMoreFilterItems';

export const CONTENT_SORT_ITEMS = [
  getRelevanceSortItem(),
  getLastEditedSortItem(),
  getAlphabeticallySortItem(),
  getNewestSortItem(),
  getOldestSortItem(),
];

export const CONTENT_SORT_DEFAULT_VALUE = getDefaultSortValue(CONTENT_SORT_ITEMS);

export default function getSortAndFilterItems(
  filter: LearningContentPageFilters,
  t: TFunction,
  appConfig: AppConfig,
  featureFlags: FeatureFlagsMap,
): SortAndFilterItem[] {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: [LearningContentPageFilters.ACTIVE, LearningContentPageFilters.ARCHIVED].includes(
        filter,
      ),
      items: CONTENT_SORT_ITEMS,
    },
    {
      type: FilterManager.Filter,
      id: 'types',
      defaultPlaceholder: t('Common:Filter.TypeOfContent'),
      isEnabled: ![LearningContentPageFilters.DRAFT].includes(filter),
      groups: [
        {
          id: 'resourceType',
          options: getContentTypeOptions(t, appConfig, featureFlags),
        },
      ],
    },
    {
      type: FilterManager.Filter,
      id: 'status',
      defaultPlaceholder: t('Common:Sort.Status'),
      isEnabled: ![LearningContentPageFilters.DRAFT].includes(filter),
      groups: [
        {
          id: 'resourceStatus',
          options: getEnabledItems([
            {
              Icon: FileOutline,
              value: ResourceStatusEnum.Draft,
              label: t('Admin:Status.DRAFT'),
              enabled: [
                LearningContentPageFilters.ACTIVE,
                LearningContentPageFilters.RECENT,
              ].includes(filter),
            },
            {
              Icon: PaperPlane,
              value: ResourceStatusEnum.Published,
              label: t('Common:Filter.Published'),
              enabled: [
                LearningContentPageFilters.ACTIVE,
                LearningContentPageFilters.RECENT,
              ].includes(filter),
            },
            {
              Icon: Undo2Outline,
              value: ResourceStatusEnum.UnpublishedChanges,
              label: t('Admin:Status.UNPUBLISHED_CHANGES'),
              enabled: [
                LearningContentPageFilters.ACTIVE,
                LearningContentPageFilters.RECENT,
              ].includes(filter),
            },
            {
              Icon: Archive,
              value: ResourceStatusEnum.Archived,
              label: t('Common:Filter.Archived'),
              enabled: [LearningContentPageFilters.ARCHIVED].includes(filter),
            },
            {
              Icon: SlashOutline,
              value: ResourceStatusEnum.Revoked,
              label: t('Common:Filter.Revoked'),
              enabled: [LearningContentPageFilters.ARCHIVED].includes(filter),
            },
          ]),
        },
      ],
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'skillIds',
      defaultPlaceholder: t('Common:Filter.Skills'),
      entity: 'skill',
      listConfig: SkillEntityConfig,
    },
    {
      type: FilterManager.Nested,
      id: 'moreFilters',
      defaultPlaceholder: t('Common:Filter.AddFilter'),
      isEnabled: true,
      listConfig: {
        listVariant: 'single-ungrouped-finite',
        useOptionsQuery: getUseOptionsQuery([
          {
            id: 'more',
            options: getMoreFilterOptions(t),
          },
        ]),
        useFilterValues: useFilterDummyValue,
      },
      uiConfig: {
        isSearchEnabled: false,
      },
      headerProps: {
        label: t('Common:Filter.AddFilter'),
      },
      pillsConfig: pillsConfig(t),
    },
  ];
}
